import { isPast } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useMemo, VFC } from 'react';

import LinkButton from '@/components/CustomButton/LinkButton';
import FramedIcon from '@/components/FramedIcon';
import ReleaseReminderSetButton from '@/components/ReleaseReminderSetButton';
import routes from '@/config/routes';
import { HOST_NAME } from '@/constants/hosts';
import LockIcon from '@/icons/lock-icon.svg';
import TwitterIcon from '@/icons/twitter.svg';

import styles from './MarketplaceReservedItemOverlay.module.scss';

type Props = {
  creationID: number;
  reservedAt: Date;
  creator: {
    userName: string;
    displayName: string;
    profileImageURL: string | null;
  };
};

const MarketplaceReservedItemOverlay: VFC<Props> = ({
  creationID,
  reservedAt,
  creator,
}: Props) => {
  const { t, i18n } = useTranslation();

  // Guess current user's time zone
  const currentUsersTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  let displayTimeZone = 'UTC';
  if (currentUsersTimeZone === 'Asia/Tokyo') {
    // Display in JST only if user is in Japan
    displayTimeZone = 'JST';
  }

  const zonedTime = utcToZonedTime(reservedAt, displayTimeZone);

  const reservationDate = useMemo(() => {
    // Guess current user's time zone
    return format(reservedAt, 'LLL d, yyyy', {
      timeZone: displayTimeZone,
    });
  }, [reservedAt, displayTimeZone]);

  const reservationTime = useMemo(() => {
    if (format(zonedTime, 'mm') === '00') {
      // Omit minutes if date is on hour
      return `at ${format(zonedTime, 'K a')} ${displayTimeZone}`;
    }
    return `at ${format(zonedTime, 'K:mm a')} ${displayTimeZone}`;
  }, [reservedAt, displayTimeZone]);

  const tweetPostURL = useMemo(() => {
    const tweetText =
      i18n.language === 'ja'
        ? `新しい作品がもうすぐ発売されます！\n最新情報をこちらからチェックしてください！ \n\n ${HOST_NAME}/ja/@${creator.userName}\n\n #ANIFTY #nftart #NFT`
        : `Hey guys! I found a new artwork. It will be on sale soon:\n\n ${HOST_NAME}/@${creator.userName}\n\n #ANIFTY #nftart #NFT`;

    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      tweetText
    )}`;
  }, [creator.userName]);

  if (isPast(reservedAt)) {
    return null;
  }

  return (
    <div className={styles.reservedItem}>
      <LockIcon className={styles.lockIcon} />

      <div className={styles.releaseDate}>
        {t('reservedItemOverlay.heading')}
        <div className={styles.theDate}>
          {reservationDate}
          <span>{reservationTime}</span>
        </div>
      </div>

      {/* Link to creator page */}
      <Link href={routes.creatorDetail(creator.userName)}>
        <a className={styles.linkToCreatorPage}>
          {creator.profileImageURL && (
            <FramedIcon imageURL={creator.profileImageURL} certified />
          )}
          {creator.displayName}
        </a>
      </Link>

      {/* Release Reminder Set button */}
      <ReleaseReminderSetButton creationID={creationID} />

      {/* Twitter share button */}
      <LinkButton
        className={styles.shareTwitterButton}
        variant="primaryOutlined"
        href={tweetPostURL}
        target="_blank"
        rel="noreferer"
      >
        <TwitterIcon className={styles.twitterIcon} />
        {t('reserveCompleteModal.shareTwitterButton')}
      </LinkButton>
    </div>
  );
};

export default MarketplaceReservedItemOverlay;
